import { useMemo, useState } from "react";
import { AccordionItemData } from "./AccordionListPanel";

export interface UseAccordionListPanelProps<T> {
  items: AccordionItemData<T>[];
  filteredItems: AccordionItemData<T>[];
  expanded: string[];
  withSelection?: boolean;
  showActionsOnHover?: boolean;
}

export interface UseAccordionListPanelResult {
  hasItems: boolean;
  hasFilteredItems: boolean;
  isItemExpanded: (itemId: string) => boolean;
  isItemSelected: (itemId: string) => boolean;
  allVisibleSelected: boolean;
  selectedIds: string[];
  selectedCount: number;
  toggleItem: (id: string) => void;
  toggleSelectAll: () => void;
  stop: {
    onClick: (e: any) => void;
    onMouseDown: (e: any) => void;
    onPointerDown: (e: any) => void;
    onKeyDown: (e: any) => void;
  };
  css: string;
}

export function useAccordionListPanel<T>(props: UseAccordionListPanelProps<T>): UseAccordionListPanelResult {
  const { items, filteredItems, expanded, withSelection = false, showActionsOnHover = false } = props;

  const hasItems = useMemo(() => items.length > 0, [items]);
  const hasFilteredItems = useMemo(() => filteredItems.length > 0, [filteredItems]);

  const isItemExpanded = useMemo(() => (itemId: string) => expanded.includes(itemId), [expanded]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const visibleIds = useMemo(() => filteredItems.map((i) => i.id), [filteredItems]);

  const allVisibleSelected = withSelection && visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleItem = (id: string) => {
    if (!withSelection) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!withSelection) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const isItemSelected = (itemId: string) => selected.has(itemId);
  const selectedIds = Array.from(selected);
  const selectedCount = selectedIds.length;

  const stop = {
    onClick: (e: any) => e.stopPropagation(),
    onMouseDown: (e: any) => e.stopPropagation(),
    onPointerDown: (e: any) => e.stopPropagation(),
    onKeyDown: (e: any) => {
      if (e.key === " " || e.key === "Enter") e.stopPropagation();
    },
  };

  const css = `
    .alp-item {
      position: relative;
      border-radius: 4px;
      margin-bottom: 4px;
    }

    .alp-item.alp-expanded {
      background-color: #E3F2FD;
    }

    .alp-item.alp-expanded .fui-AccordionHeader,
    .alp-item.alp-expanded .fui-AccordionPanel {
      background-color: transparent !important;
    }

    .alp-actions {
      margin-left: auto;        /* push actions all the way right */
      display: flex;            /* allow proper vertical centering */
      align-items: center;      /* vertically center icons + "|" */
      gap: 6px;
      margin-left: 6px;
      visibility: hidden;
      transition: visibility 120ms ease-in-out;
      flex: 0 0 auto;
    }

    .alp-item .fui-AccordionHeader__button > div {
      min-width: 0;
     }

    .alp-actions span {
      display: flex;            /* center icons in their square box */
      align-items: center;
    }

    ${showActionsOnHover ? `.alp-item:hover .alp-actions { visibility: visible; }` : ""}

    .alp-item[data-expanded="true"] .alp-actions {
      visibility: visible;
    }
  `;

  return {
    hasItems,
    hasFilteredItems,
    isItemExpanded,
    isItemSelected,
    allVisibleSelected,
    selectedIds,
    selectedCount,
    toggleItem,
    toggleSelectAll,
    stop,
    css,
  };
}
